import type { IntakePacket, IntakeAnswer, IntakeGoalDraft, IntakeTrackerDraft, IntakeSection } from '@/types/intake';
import { getSupabase } from '@/lib/supabase';
import { api, throwIfNotOk, withStatus } from '@/lib/apiClient';
import { asError } from '@/lib/sentry';

/**
 * Supabase Storage Adapter for Intake Data
 *
 * ENCRYPTION NOTE: Intake answers contain PII (name, age, weight, injuries, etc.)
 * and are encrypted server-side in the Worker API (/api/clients/intake).
 *
 * - WRITES: Use the Worker API POST endpoint for encrypted writes
 * - READS: Worker API GET decrypts values automatically
 * - Legacy plaintext data is still supported via fallback
 *
 * The encrypted columns are: value_encrypted, value_nonce, value_key_id
 * The plaintext column (value) is deprecated and will be null for new data.
 */

// Column set used to embed related rows for a single round-trip
// Note: value may be null for encrypted answers (decryption happens via API)
const PACKET_COLS = `
  id, invite_id, coach_id, client_email, client_id, status, program_template_id, nutrition_template_id,
  created_at, submitted_at, merged_at,
  intake_answers ( id, section, key, value, value_encrypted, value_nonce, value_key_id, created_at, updated_at ),
  intake_goals   ( * ),
  intake_trackers( * )
`;

function nowISO(): string {
  return new Date().toISOString();
}
function uid(prefix: string): string {
  return globalThis.crypto?.randomUUID?.() || `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}
async function completePacketViaApi(
  packetId: string,
  action: 'submit' | 'merge',
  clientId?: string,
): Promise<IntakePacket> {
  const res = await api.post<unknown>('/api/onboarding/complete', { packetId, action, clientId });
  const packet = res.ok ? (res as { packet?: unknown }).packet : undefined;
  if (!res.ok || !packet) {
    throw new Error(res.message || (res.ok ? 'API error: 200' : withStatus(res, (status) => `API error: ${status}`)));
  }
  return mapPacket(packet);
}

// latest-wins by (updated_at || created_at)
// Note: handles both plaintext (legacy) and encrypted values
// Encrypted values show as '[encrypted]' when read directly - use API for decryption
function foldAnswersBySection(answers: any[] | undefined) {
  const out: Record<string, Record<string, any>> = {};
  const times: Record<string, Record<string, string>> = {};
  for (const a of answers || []) {
    const sec = a.section || 'misc';
    const k = a.key;
    const t = a.updated_at || a.created_at || '';
    if (!out[sec]) {
      out[sec] = {};
      times[sec] = {};
    }
    if (!times[sec][k] || (t && t > times[sec][k])) {
      // Use decryptedValue if available (from API), otherwise plaintext, otherwise mark as encrypted
      const value = a.decryptedValue ?? a.value ?? (a.value_encrypted ? '[encrypted]' : null);
      out[sec][k] = value;
      times[sec][k] = t;
    }
  }
  return out;
}

function mapPacket(row: any): IntakePacket {
  const answers = row?.intake_answers || [];
  const bySection = foldAnswersBySection(answers);
  const base: any = {
    id: row.id,
    inviteId: row.invite_id,
    coachId: row.coach_id,
    clientEmail: row.client_email,
    programTemplateId: row.program_template_id ?? null,
    nutritionTemplateId: row.nutrition_template_id ?? null,
    status: row.status,
    createdAt: row.created_at,
    submittedAt: row.submitted_at || undefined,
    mergedAt: row.merged_at || undefined,
    clientId: row.client_id || undefined,
  };
  // Expose all sections for Intake Review UI
  if (bySection.form_flags) base.formFlags = bySection.form_flags;
  if (bySection.personal) base.personal = bySection.personal;
  if (bySection.experience) base.experience = bySection.experience;
  if (Array.isArray(row?.intake_goals)) base.goals = (row.intake_goals || []).map(mapGoal);
  if (Array.isArray(row?.intake_trackers)) base.trackers = (row.intake_trackers || []).map(mapTracker);
  return base as IntakePacket;
}

function mapAnswer(row: any): IntakeAnswer {
  // Use decryptedValue if available (from API), otherwise plaintext, otherwise mark as encrypted
  const value = row.decryptedValue ?? row.value ?? (row.value_encrypted ? '[encrypted]' : null);
  return {
    id: row.id,
    packetId: row.packet_id,
    section: row.section,
    key: row.key,
    value,
    createdAt: row.created_at || nowISO(),
    // Include encryption metadata for debugging (not exposed in type, but useful)
    ...(row.value_key_id ? { keyId: row.value_key_id } : {}),
  } as IntakeAnswer;
}

function mapGoal(row: any): IntakeGoalDraft {
  return {
    id: row.id,
    packetId: row.packet_id,
    title: row.title,
    unit: row.unit ?? null,
    targetValue: row.target_value ?? null,
    priority: row.priority ?? 'medium',
    createdAt: row.created_at || nowISO(),
  } as IntakeGoalDraft;
}

function mapTracker(row: any): IntakeTrackerDraft {
  return {
    id: row.id,
    packetId: row.packet_id,
    title: row.title,
    unit: row.unit ?? null,
    cadence: row.cadence ?? 'weekly',
    reminders: !!row.reminders,
    showInAnalytics: !!row.show_in_analytics,
    createdAt: row.created_at || nowISO(),
  } as IntakeTrackerDraft;
}

export async function createPacket(inviteId: string, coachId: string, clientEmail: string): Promise<IntakePacket> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { data, error } = await sb
    .from('intake_packets')
    .insert({
      invite_id: inviteId,
      coach_id: coachId,
      client_email: String(clientEmail).toLowerCase(),
      status: 'draft',
    })
    .select('*')
    .single();
  if (error) throw asError(error);
  return mapPacket(data);
}

export async function getPacketByInvite(inviteId: string): Promise<IntakePacket | null> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { data: pkt, error } = await sb
    .from('intake_packets')
    .select(PACKET_COLS)
    .eq('invite_id', inviteId)
    .limit(1)
    .maybeSingle();
  if (error) throw asError(error);
  if (!pkt) return null;
  return mapPacket(pkt);
}

export async function getPacketById(id: string): Promise<IntakePacket | null> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { data: pkt, error } = await sb.from('intake_packets').select(PACKET_COLS).eq('id', id).single();
  if (error) throw asError(error);
  if (!pkt) return null;
  return mapPacket(pkt);
}

/**
 * Upsert an intake answer. The Worker API encrypts it server-side; the browser has no keys.
 *
 * There is deliberately NO direct-Supabase fallback. The old one wrote the answer as PLAINTEXT
 * whenever the API failed for any reason (5xx, network, even 4xx validation), and RLS did not
 * block it (permissive intake policies OR past the encryption guard). Client health answers were
 * still being stored in plaintext in prod (Sec2 / DATA-19, L3). On failure this now throws; the
 * onboarding `debounced()` wrapper shows the error and logs it.
 */
export async function upsertAnswer(
  packetId: string,
  section: IntakeSection,
  key: string,
  value: unknown,
): Promise<IntakeAnswer> {
  const res = throwIfNotOk(await api.post<unknown>('/api/clients/intake', { packetId, section, key, value }));
  return {
    id: (res as { answerId?: string }).answerId || crypto.randomUUID(),
    packetId,
    section,
    key,
    value,
    createdAt: nowISO(),
  } as IntakeAnswer;
}

export async function addGoalDraft(
  packetId: string,
  goal: Omit<IntakeGoalDraft, 'id' | 'packetId' | 'createdAt'>,
): Promise<IntakeGoalDraft> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const row = {
    id: uid('goal'),
    packet_id: packetId,
    title: goal.title,
    unit: goal.unit ?? null,
    target_value: goal.targetValue ?? null,
    priority: goal.priority ?? 'medium',
    created_at: nowISO(),
  };
  const { data, error } = await sb.from('intake_goals').insert(row).select('*').single();
  if (error) throw asError(error);
  return mapGoal(data);
}

export async function removeGoalDraft(id: string): Promise<boolean> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { error, count } = await sb.from('intake_goals').delete({ count: 'exact' }).eq('id', id);
  if (error) throw asError(error);
  return (count || 0) > 0;
}

export async function listGoalsForPacket(packetId: string): Promise<IntakeGoalDraft[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { data, error } = await sb.from('intake_goals').select('*').eq('packet_id', packetId);
  if (error) throw asError(error);
  return (data || []).map(mapGoal);
}

export async function addTrackerDraft(
  packetId: string,
  tracker: Omit<IntakeTrackerDraft, 'id' | 'packetId' | 'createdAt'>,
): Promise<IntakeTrackerDraft> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const row = {
    id: uid('trk'),
    packet_id: packetId,
    title: tracker.title,
    unit: tracker.unit ?? null,
    cadence: tracker.cadence ?? 'weekly',
    reminders: !!tracker.reminders,
    show_in_analytics: !!tracker.showInAnalytics,
    created_at: nowISO(),
  };
  const { data, error } = await sb.from('intake_trackers').insert(row).select('*').single();
  if (error) throw asError(error);
  return mapTracker(data);
}

export async function removeTrackerDraft(id: string): Promise<boolean> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { error, count } = await sb.from('intake_trackers').delete({ count: 'exact' }).eq('id', id);
  if (error) throw asError(error);
  return (count || 0) > 0;
}

export async function listTrackersForPacket(packetId: string): Promise<IntakeTrackerDraft[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { data, error } = await sb.from('intake_trackers').select('*').eq('packet_id', packetId);
  if (error) throw asError(error);
  return (data || []).map(mapTracker);
}

// Normalized drafts accessor used by coach editor (always returns array)
export async function listTrackerDrafts(packetId: string): Promise<any[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { data, error } = await sb
    .from('intake_trackers')
    .select('*')
    .eq('packet_id', packetId)
    .order('created_at', { ascending: true });
  if (error) return [] as any[];
  return (data || []).map((r: any) => ({
    id: r.id,
    packetId: r.packet_id,
    title: r.title || '',
    unit: r.unit || '',
    cadence: (r.cadence || 'weekly') as 'daily' | 'weekly',
    days: [], // days column not present yet
    reminders: !!r.reminders,
    showInAnalytics: !!r.show_in_analytics,
    createdAt: r.created_at,
  }));
}

export async function upsertTrackerDraft(packetId: string, d: any): Promise<any> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const title = String(d?.title || '').trim();
  if (!title) throw new Error('title required');
  // try find existing by packet+title (case-insensitive)
  const { data: existing } = await sb
    .from('intake_trackers')
    .select('id, title')
    .eq('packet_id', packetId)
    .ilike('title', title)
    .limit(1);

  const row: any = {
    packet_id: packetId,
    title,
    unit: d?.unit || '',
    cadence: d?.cadence || 'weekly',
    reminders: !!d?.reminders,
    show_in_analytics: !!d?.showInAnalytics,
  };

  if (existing && existing.length) {
    const keepId = existing[0].id;
    const { data: upd, error: uerr } = await sb
      .from('intake_trackers')
      .update(row)
      .eq('id', keepId)
      .select('*')
      .single();
    if (uerr) throw uerr;
    const dupes = (existing.slice(1) || []).map((x) => x.id);
    if (dupes.length) await sb.from('intake_trackers').delete().in('id', dupes);
    const all = await listTrackerDrafts(packetId);
    return (
      all.find((t) => t.id === (upd as any)?.id) ||
      all.find((t) => (t.title || '').toLowerCase() === title.toLowerCase()) ||
      null
    );
  } else {
    const { data: ins, error: ierr } = await sb.from('intake_trackers').insert(row).select('*').single();
    if (ierr) throw ierr;
    const all = await listTrackerDrafts(packetId);
    return (
      all.find((t) => t.id === (ins as any)?.id) ||
      all.find((t) => (t.title || '').toLowerCase() === title.toLowerCase()) ||
      null
    );
  }
}

export async function deleteTrackerDraft(packetId: string, idOrTitle: string): Promise<boolean> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  // try delete by id; if none removed, fallback by title
  const byId = await sb
    .from('intake_trackers')
    .delete({ count: 'exact' })
    .eq('packet_id', packetId)
    .eq('id', idOrTitle);
  if ((byId.count || 0) === 0) {
    await sb.from('intake_trackers').delete().eq('packet_id', packetId).ilike('title', String(idOrTitle));
  }
  return true;
}

export async function reassignInvite(packetId: string, newInviteId: string): Promise<any> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  // If a packet already exists with this invite_id, drop it only if empty
  const { data: existing } = await sb
    .from('intake_packets')
    .select('id')
    .eq('invite_id', newInviteId)
    .limit(1)
    .maybeSingle();

  if (existing?.id && existing.id !== packetId) {
    const ansCnt = await sb
      .from('intake_answers')
      .select('id', { count: 'exact', head: true })
      .eq('packet_id', existing.id);
    const trkCnt = await sb
      .from('intake_trackers')
      .select('id', { count: 'exact', head: true })
      .eq('packet_id', existing.id);
    const ans = ansCnt.count || 0;
    const trk = trkCnt.count || 0;
    if (ans === 0 && trk === 0) {
      await sb.from('intake_packets').delete().eq('id', existing.id);
    } else {
      throw new Error('Target invite already has a non-empty packet');
    }
  }

  const { error } = await sb.from('intake_packets').update({ invite_id: newInviteId }).eq('id', packetId);
  if (error) throw asError(error);

  const { data, error: e2 } = await sb.from('intake_packets').select('id, invite_id').eq('id', packetId).single();
  if (e2) throw e2;
  return data;
}

export async function submitPacket(packetId: string): Promise<void> {
  await completePacketViaApi(packetId, 'submit');
}

export async function listAnswersForPacket(packetId: string): Promise<IntakeAnswer[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { data, error } = await sb
    .from('intake_answers')
    .select('*')
    .eq('packet_id', packetId)
    .order('section', { ascending: true })
    .order('key', { ascending: true })
    .order('updated_at', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw asError(error);
  return (data || []).map(mapAnswer);
}

export async function deleteAnswer(packetId: string, section: string, key: string): Promise<boolean> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { error } = await sb
    .from('intake_answers')
    .delete()
    .eq('packet_id', packetId)
    .eq('section', section)
    .eq('key', key);
  if (error) throw asError(error);
  return true;
}

export async function markMerged(packetId: string, clientId?: string): Promise<IntakePacket> {
  return completePacketViaApi(packetId, 'merge', clientId);
}

export async function setMotivation(packetId: string, value: string | null) {
  return upsertAnswer(packetId, 'personal' as any, 'motivation', value as any);
}

export async function setInjuries(packetId: string, value: string | null) {
  return upsertAnswer(packetId, 'personal' as any, 'injuries', value as any);
}
